import React, { ChangeEvent, useEffect, useState } from "react";
import { MdArrowBackIosNew } from "react-icons/md";
import AuthButton from "../../pages/LogInPage/LoginButton";
import { closeDialog } from "../../redux/Slices/dialogSlice";
import { useDispatch, useSelector } from "react-redux";
import { BiCheckDouble, BiSave, BiX } from "react-icons/bi";
import CustomTextField from "../ReUsableComponents/CustomTextField";
import Dropdown from "../../pages/LogInPage/Dropdown";
import {
  useFetchUserQuery,
  useUpdateExistingUserMutation,
} from "../../redux/API/Login/loginApi";
import {
  useCountyGetAllQuery,
  useLazyGetCountySchoolByIdQuery,
} from "../../redux/API/ProfileSetup/profileSetupApi";
import {
  convertToUSANumber,
  formatPhoneNumberToInternational,
  handleTokenChanges,
  usePhoneValidation,
} from "../../utils";
import { GrCircleAlert } from "react-icons/gr";
import { getItem } from "../../utils/token";
import { RootState } from "../../redux/Store/store";
import { toast } from "react-toastify";
import { showSuccessToast } from "../../utils/commonLogic";

type UserDetailsData = {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  role: string;
  county: string;
  school: string;
  ifFreeText:boolean;
  schoolInfo:string;
};

const EditProfile = () => {
  const dispatch = useDispatch();
  const { validatePhone } = usePhoneValidation();

  const {
    data: userData,
    isLoading: userDataLoading,
    isSuccess: userDataSuccess,
    isError: userDataError,
  } = useFetchUserQuery({}, { refetchOnMountOrArgChange: true });
  useCountyGetAllQuery({}, { refetchOnMountOrArgChange: true } );
  const [getCountySchoolById] = useLazyGetCountySchoolByIdQuery();
  const [updateExistingUser] = useUpdateExistingUserMutation();

  const [userDetails, setUserDetails] = useState<UserDetailsData>({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    role: "",
    county: "",
    school: "",
    ifFreeText:false,
    schoolInfo: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({
    firstName: false,
    lastName: false,
    email: false,
    mobile: false,
    role: false,
    county: false,
    school: false,
    schoolInfo: false,
  });
  const [isCountyDropdownOpen, setIsCountyDropdownOpen] = useState(false);
  const [isSchoolDropdownOpen, setIsSchoolDropdownOpen] = useState(false);
  const [schoolData, setSchoolData] = useState();
  const [saveLoading, setSaveLoading] = useState(false);
  const [apiError, setApiError] = useState(false);

  const {countyData} = useSelector((state:RootState) => state.root.profileSetupSlice)

  const isCountyOthers = countyData?.find((item : any) => item.id === userDetails.county)?.countyName === "Others";

  const handleCloseDialog = () => {
    dispatch(closeDialog());
  };

  const handleDropdownChange = async (val: any, name: string) => {
    if (name === "county" && val?.id !== userDetails.county) {
      setUserDetails((prev) => ({ ...prev, county: val?.id, school: "" }));
      const schools = await fetchSchool(val?.id);
      if(val?.countyName === "Others"){
        setUserDetails((prev) => ({ ...prev, school: schools[0]['id'], ifFreeText: true }));
        setErrors((prev) => ({ ...prev, schoolInfo: true }));
      }else{
        setUserDetails((prev) => ({ ...prev, schoolInfo: '', ifFreeText: false }));
        setErrors((prev) => ({ ...prev, school: true }));
      }
    } else if (name === "school") {
      setUserDetails((prev) => ({ ...prev, school: val?.id }));
      setErrors((prev) => ({ ...prev, school: false }));
    }
    setIsCountyDropdownOpen(false);
    setIsSchoolDropdownOpen(false);
  };

  const fetchSchool = async (countyId: string) => {
    if (countyId) {
      const school = await getCountySchoolById(countyId).unwrap();
      setSchoolData(school?.data);
      return school.data;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
    let formattedNumber = input;

    if (input.length <= 3) {
      formattedNumber = input; // No formatting for 1-3 digits
    } else if (input.length <= 6) {
      formattedNumber = `(${input.slice(0, 3)}) ${input.slice(3)}`;
    } else {
      formattedNumber = `(${input.slice(0, 3)}) ${input.slice(
        3,
        6
      )}-${input.slice(6, 10)}`;
    }

    setUserDetails((prev) => ({ ...prev, mobile: formattedNumber }));

    const isPhoneValid = validatePhone(formattedNumber);

    if (!isPhoneValid) {
      setErrors((prev) => ({ ...prev, mobile: true }));
    } else {
      setErrors((prev) => ({ ...prev, mobile: false }));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "firstName" || name === "lastName" || name === "schoolInfo") {
      setUserDetails((prev) => ({ ...prev, [name]: value }));
      if (value.trim() === "") {
        setErrors((prev) => ({ ...prev, [name]: true }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: false }));
      }
    }
  };

  const getSelectedName = (data: any, selected: any, name: string) => {
    const selectedItem = data?.find((item: any) => item.id === selected);
    if (name === "county") {
      return selectedItem ? selectedItem.countyName : "";
    }
    if (name === "school") {
      return selectedItem ? selectedItem.schoolName : "";
    }
    return "";
  };

  const getSaveBtnDisabled = () => {
    const userRole = getItem("userRole");
    if (
        !userDetails.firstName ||
        !userDetails.lastName ||
        !userDetails.email ||
        (userRole !== "PARENT" && (!userDetails.county || !userDetails.school || (isCountyOthers && !userDetails.schoolInfo)))
      ) {
        return true;
      }

    const isError: boolean = Object.keys(errors).some((key) => errors[key]);
    if (isError) {
      return true;
    }

    return false;
  };

  const handleSave = async () => {
    try {
        let payload = {
          firstName: userDetails.firstName.trim(),
          lastName: userDetails.lastName.trim(),
          mobile: `+1${formatPhoneNumberToInternational(userDetails.mobile)}`,
          role: userDetails.role,
          county: userDetails.county,
          school: userDetails.school,
          ifFreeText: userDetails.ifFreeText,
          schoolInfo: userDetails.schoolInfo.trim(),
        };
        setSaveLoading(true);
        let response = await updateExistingUser(payload).unwrap();
        if (response.status === "success") {
          handleTokenChanges(response?.data?.token);
          setSaveLoading(false);
          dispatch(closeDialog());
          setApiError(false);
          showSuccessToast('Profile changes have been saved', <BiCheckDouble className="w-6 h-6 text-satPrepGreen" />);
        } else {
          setApiError(true);
          setSaveLoading(false);
        }
    } catch (error) {
        setApiError(true);
        setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (userDataSuccess) {
      const tempData: UserDetailsData = {
        firstName: userData.data?.firstName ?? "",
        lastName: userData.data?.lastName ?? "",
        email: userData.data?.email ?? "",
        mobile: convertToUSANumber(userData.data?.mobile) ?? "",
        role: userData.data?.role ?? "",
        county: userData.data?.county ?? "",
        school: userData.data?.school ?? "",
        ifFreeText: userData.data?.ifFreeText ?? userData.data?.schoolInfo?.trim() !== "",
        schoolInfo: userData.data?.schoolInfo ?? "",
      };
      fetchSchool(userData.data?.county);
      setUserDetails(tempData);
      setApiError(false);
    }
    if(userDataError){
        setApiError(true);
    }
  }, [userDataLoading, userDataSuccess, userData]);

  

  return (
    <div className="flex flex-col items-center p-6 gap-9  pb-9 bg-satPrepFrostedWhite backdrop-blur-[20px] shadow-dialog border-[0.5px] border-satPrepGrey rounded-[28px]">
      <h1 className="font-gully text-[40px] tracking-wide font-bold text-center text-satPrepAccent ">
        Edit profile
      </h1>
      <div className="w-full">
        {apiError && (
          <div className="flex w-full items-center py-2 px-2 border border-[#d00000] rounded-xl">
            <div>
              <GrCircleAlert size={24} className="text-[#D00000] mr-[10px]" />
            </div>
            <p className="text-[#D00000] text-sm font-medium">
              There was an issue, please wait a moment and try again.
            </p>
          </div>
        )}
        <CustomTextField
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(e)
          }
          placeholder="First Name"
          type={"text"}
          name="firstName"
          mandatory={true}
          value={userDetails?.firstName ?? ""}
          isError={errors.firstName}
          className={"w-full !border-[#646464] mt-6 font-gully font-medium pt-3"}
        />
        <CustomTextField
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(e)
          }
          placeholder="Last Name"
          type={"text"}
          name="lastName"
          mandatory={true}
          value={userDetails?.lastName ?? ""}
          isError={errors.lastName}
          className={"w-full !border-[#646464] mt-6 font-gully font-medium pt-3"}
        />
        <CustomTextField
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(e)
          }
          placeholder="Email"
          type={"text"}
          name="email"
          value={userDetails?.email ?? ""}
          className={"w-full !border-[#646464] mt-6 font-gully font-medium pt-3"}
          disabled
        />
        {getItem("userRole") === "STUDENT" && userData?.data?.role && (
          <div className="font-gully font-medium">
            <div className="mt-6">
              <Dropdown
                getOption={(option: any) => `${option.countyName}`}
                onChangeHandler={handleDropdownChange}
                setOpen={setIsCountyDropdownOpen}
                open={isCountyDropdownOpen}
                selected={getSelectedName(
                  countyData ?? [],
                  userDetails?.county,
                  "county"
                )}
                options={countyData ?? []}
                isError={errors.county}
                label="County"
                name="county"
              />
            </div>
            <div className="mt-6">
              { isCountyOthers  ? 
                (<CustomTextField
                  onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(e)
                  }
                  placeholder="School"
                  type={"text"}
                  name="schoolInfo"
                  value={userDetails?.schoolInfo}
                  className="w-full !border-[#646464]"
                  isError={errors.schoolInfo}
                  containerClass="mt-5"
                />) : 
                (<Dropdown
                  getOption={(option: any) => `${option.schoolName}`}
                  onChangeHandler={handleDropdownChange}
                  setOpen={setIsSchoolDropdownOpen}
                  open={isSchoolDropdownOpen}
                  selected={getSelectedName(
                    schoolData,
                    userDetails?.school,
                    "school"
                  )}
                  options={schoolData || []}
                  isError={errors.school}
                  label="School"
                  name="school"
                />)
              }
            </div>
          </div>
        )}

        <CustomTextField
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
            handlePhoneChange(e)
          }
          placeholder="Phone Number"
          type={"text"}
          name="mobile"
          value={userDetails?.mobile ?? ""}
          className="w-full !border-[#646464] font-gully font-medium pt-3"
          isError={errors.mobile}
          containerClass="mt-6"
        />
      </div>

      <div className="flex justify-between items-center self-stretch">
        <AuthButton
          text="Back"
          icon={<MdArrowBackIosNew size={16}/>}
          iconPosition="left"
          onClick={handleCloseDialog}
          loader={false}
          disabled={false}
        />
        <AuthButton
          text="Save"
          icon={<BiSave size={24}/>}
          iconPosition="right"
          onClick={handleSave}
          loader={saveLoading}
          disabled={getSaveBtnDisabled() || saveLoading}
        />
      </div>
    </div>
  );
};

export default EditProfile;
